import { apiGet, apiPut, apiRequest, API_URL } from "@/lib/api/client";
import { mockVehicles } from "@/lib/mock-data";
import { positionsService, type TraccarPosition } from "@/services/positions.service";
import { authService } from "@/services/auth.service";
import { mergeIconAttributes, readIconAttributes } from "@/lib/traccar-device-attributes";
import { resolveVehicleModel } from "@/lib/vehicle-icons";
import { runInBatches } from "@/lib/concurrency";
import { gpsDriveGet } from "@/lib/api/gpsdrive-client";
import type { Position, Vehicle, VehicleStatus } from "@/types";

// Formato bruto devolvido pelo Traccar em GET /api/devices. Exportado porque
// o WebSocket de tempo real (use-realtime-sync.ts) manda devices no MESMO
// formato bruto (protocolo documentado do Traccar) e precisa do mesmo tipo
// pra aplicar o patch incremental no cache (ver applyRealtimeUpdate abaixo).
export interface TraccarDevice {
  id: number;
  name: string;
  uniqueId: string;
  status: "online" | "offline" | "unknown";
  lastUpdate: string | null;
  positionId: number;
  phone?: string;
  attributes?: { plate?: string; driver?: string; [key: string]: unknown };
}

// Abaixo dessa velocidade (km/h), um veículo "online" é considerado parado
// quando a posição não informa o atributo "motion" explicitamente.
const IDLE_SPEED_THRESHOLD_KMH = 2;
// Abaixo dessa porcentagem, a bateria do rastreador é considerada baixa
// quando o alarme "lowBattery" não é reportado explicitamente.
const LOW_BATTERY_THRESHOLD_PCT = 20;

// O Traccar só marca um device como "offline" depois do próprio timeout
// interno dele expirar — nesse meio-tempo ele continua devolvendo
// status "online" mesmo que o rastreador já tenha parado de reportar há
// minutos (bug relatado 2026-08-31: veículo J17 offline aparecendo verde).
// Aqui reforçamos isso checando a idade do último reporte diretamente.
const OFFLINE_STALENESS_MS = 5 * 60 * 1000;

// Tag (tipo de rastreador "tag" no cadastro do veículo) reporta com pouca
// frequência (bateria) — o timeout acima, e o próprio status do Traccar,
// marcavam tag offline o tempo todo. Pedido 2026-09-23: tag só fica
// offline depois de 24h REAIS sem comunicação, e parada continua "online"
// (nunca "idle"). Mesma regra no backend (vehicle-metadata/tracker-type.ts).
const TAG_OFFLINE_MS = 24 * 60 * 60 * 1000;
const TAG_IDS_CACHE_KEY = "gpsdrive.tagDeviceIds.v1";
const TAG_IDS_REFRESH_MS = 2 * 60 * 1000;

function readTagIdsCache(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(TAG_IDS_CACHE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

// Seedado do localStorage — no F5 a tag já nasce com o status certo, sem
// piscar "offline" até a primeira resposta do backend.
let tagDeviceIds = readTagIdsCache();
let tagIdsFetchedAt = 0;
let tagIdsInFlight: Promise<void> | null = null;

// Nunca aguardado pelo poll da frota: roda em segundo plano e, se a lista
// de tags mudou, pede o mesmo refetch pontual do cache sticky
// (stickyUpdatedHandler). Falha (backend fora/sessão de gestão caída)
// mantém a última lista conhecida.
function refreshTagDeviceIds(force = false): Promise<void> {
  if (tagIdsInFlight) return tagIdsInFlight;
  if (!force && Date.now() - tagIdsFetchedAt < TAG_IDS_REFRESH_MS) return Promise.resolve();
  tagIdsInFlight = gpsDriveGet<number[]>("/api/v1/vehicle-metadata/tag-device-ids")
    .then((ids) => {
      tagIdsFetchedAt = Date.now();
      const next = new Set(ids.map(String));
      const changed = next.size !== tagDeviceIds.size || [...next].some((id) => !tagDeviceIds.has(id));
      tagDeviceIds = next;
      try {
        window.localStorage.setItem(TAG_IDS_CACHE_KEY, JSON.stringify([...next]));
      } catch {
        /* noop */
      }
      if (changed) stickyUpdatedHandler?.();
    })
    .catch(() => {
      /* tenta de novo no próximo poll */
    })
    .finally(() => {
      tagIdsInFlight = null;
    });
  return tagIdsInFlight;
}

function resolveStatus(device: TraccarDevice, position?: Position): VehicleStatus {
  if (tagDeviceIds.has(String(device.id))) {
    const last = device.lastUpdate ?? position?.timestamp;
    return last && Date.now() - new Date(last).getTime() < TAG_OFFLINE_MS ? "online" : "offline";
  }
  if (device.status !== "online") return "offline";
  if (device.lastUpdate && Date.now() - new Date(device.lastUpdate).getTime() > OFFLINE_STALENESS_MS) {
    return "offline";
  }
  if (!position) return "offline";
  const moving = position.motion ?? position.speed > IDLE_SPEED_THRESHOLD_KMH;
  return moving ? "online" : "idle";
}

// --- Estado "sticky" de blocked/ignition ------------------------------
//
// Vários protocolos (ex.: gt06) só incluem `blocked`/`ignition` em certos
// tipos de pacote — comprovado direto no banco do Traccar: um device gt06
// intercala pacotes de GPS (`type:18`, traz ignition), status (`type:19`,
// traz ignition + blocked) e telemetria simples (nenhum dos dois). Como
// GET /api/positions só devolve a posição mais recente por device, a
// maioria dos polls pega um pacote que simplesmente não carrega o
// atributo — sem isso, o valor "sumiria" a cada poll mesmo que nada tenha
// mudado de verdade no veículo.
//
// Aqui guardamos, por veículo, o último valor EXPLICITAMENTE confirmado
// por alguma posição (nunca inventado) e repetimos esse valor enquanto os
// pacotes seguintes não trouxerem o atributo. Assim que uma posição nova
// traz um valor explícito — inclusive invertendo true<->false — o cache
// atualiza na hora. `undefined` nunca é gravado como false/true: só existe
// enquanto o veículo nunca reportou esse atributo (nem ao vivo, nem no
// seed de histórico abaixo).
// powerVoltage entra no mesmo cache sticky por um motivo próprio: nem todo
// pacote de posição traz `power`/`adc1` (igual blocked/ignition), então sem
// isso o número piscava entre um valor e "—" a cada poll/refetch mesmo sem
// nenhuma mudança real na tensão. Guardando o último valor explícito, o
// voltímetro (frota + Sensores) só muda quando um pacote novo realmente traz
// um valor diferente.
// batteryLevel pelo mesmo motivo: nem todo pacote traz o atributo de bateria
// (mesma limitação de protocolo citada acima), então tanto o "%" em Sensores
// quanto o badge "Bateria baixa" na frota piscavam sumindo/reaparecendo a
// cada poll. batteryLow também passa a ser derivado do valor sticky, não do
// pacote cru, pelo mesmo motivo.
interface StickyState {
  blocked?: boolean;
  ignition?: boolean;
  powerVoltage?: number;
  powered?: boolean;
  batteryLevel?: number;
  // Alimentação (2026-10-19): quando a ignição mudou e quando chegou o
  // último alarme de corte — ver resolvePowered.
  ignitionChangedAt?: number;
  powerCutAt?: number;
  // Último pacote já considerado pra alimentação — o poll de 15 s reaplica
  // o MESMO pacote, que não pode renovar o alarme.
  powerPositionId?: string;
  // Último charge explícito (true/false) — ver CUT_VOLTAGE.
  charge?: boolean;
}
const stickyState = new Map<string, StickyState>();

// --- Estado bruto conhecido (device + posição), pra patch incremental --
//
// Espelha o último TraccarDevice/Position vistos por veículo — alimentado
// por todo poll completo (listFromTraccar) E por toda mensagem de
// WebSocket (ver applyRealtimeUpdate). É o que permite reconstruir um
// Vehicle sozinho (via toVehicle) quando só a posição mudou e a mensagem
// de tempo real não trouxe o device inteiro de novo (motion/status não
// mudaram, só a posição). Nunca a fonte de verdade — só um espelho local
// do último estado já visto nesta aba; o poll de 15s continua reconciliando
// contra o servidor de qualquer forma.
const lastKnownDevices = new Map<string, TraccarDevice>();
const lastKnownPositions = new Map<string, Position>();

// Códigos de alarme do Traccar (Position.attributes.alarm) que indicam
// corte/retorno da alimentação externa — usados para derivar o estado
// Ligado/Desligado de "Alimentação" em Sensores. "lowBattery" está incluído
// de propósito: neste sistema esse alarme é rotulado "Bateria violada" (ver
// ALARM_LABELS em routes/index.tsx) e é exatamente o sinal que deve marcar a
// alimentação como Desligada quando a bateria é violada.
function powerStateFromAlarm(alarm: string | undefined): boolean | undefined {
  // Traccar junta alarmes do mesmo pacote com vírgula ("lowBattery,powerCut").
  const list = (alarm ?? "").split(",").map((a) => a.trim());
  if (list.some((a) => a === "powerCut" || a === "powerOff" || a === "tamper" || a === "lowBattery")) {
    return false;
  }
  if (list.some((a) => a === "powerRestored" || a === "powerOn")) {
    return true;
  }
  return undefined;
}

// Bug 2026-10-19 ("Alimentação desligada até fechar e abrir o app"): um
// alarme de corte deixava powered=false pra sempre — o GT06 nunca manda
// "powerRestored". E o alarme costuma ser falso: vem 0–10 s depois de
// ligar/desligar o motor (queda de tensão). Mesma regra do aviso por
// WhatsApp (backend power-cut-alert-engine.ts):
//  - alarme colado numa troca de ignição (±60 s) é ignorado;
//  - charge:true num pacote = energia ligada de novo;
//  - corte de verdade o GT06 repete a cada ~5 min; sem repetição em 10 min
//    volta a "Ligada".
const IGNITION_DIP_WINDOW_MS = 60_000;
const POWER_CUT_HOLD_MS = 10 * 60_000;
// C4 MIY-7647 (2026-10-20): bateria tirada, GT06 mandou o alarme UMA vez e
// depois só 0,0 V + charge:false — em 10 min voltava a "Ligada" com o
// voltímetro em 0. Tensão medida abaixo disso, sem charge:true, = Desligada
// (renova a cada pacote novo). Só força Desligada, nunca Ligada: o adc1
// copiado pode ser de antes do corte.
const CUT_VOLTAGE = 1;

function resolvePowered(
  prev: StickyState,
  ignition: boolean | undefined,
  alarmPower: boolean | undefined,
  charge: boolean | undefined,
  voltage: number | undefined,
  positionId: string | undefined,
  now: number,
): Pick<StickyState, "powered" | "ignitionChangedAt" | "powerCutAt" | "powerPositionId" | "charge"> {
  let { powered, ignitionChangedAt, powerCutAt } = prev;
  const fresh = positionId === undefined || positionId !== prev.powerPositionId;
  if (!fresh) {
    alarmPower = undefined;
    charge = undefined;
    voltage = undefined;
  }
  const lastCharge = charge ?? prev.charge;
  if (ignition !== undefined && prev.ignition !== undefined && ignition !== prev.ignition) ignitionChangedAt = now;
  if (alarmPower === false) {
    if (ignitionChangedAt === undefined || now - ignitionChangedAt > IGNITION_DIP_WINDOW_MS) {
      powered = false;
      powerCutAt = now;
    }
  } else if (alarmPower === true || charge === true) {
    powered = true;
    powerCutAt = undefined;
  }
  if (powered === false && ignitionChangedAt !== undefined && powerCutAt !== undefined && Math.abs(powerCutAt - ignitionChangedAt) <= IGNITION_DIP_WINDOW_MS) {
    // Ignição mudou logo DEPOIS do alarme (ordem dos pacotes invertida).
    powered = undefined;
    powerCutAt = undefined;
  }
  if (powered === false && powerCutAt !== undefined && now - powerCutAt > POWER_CUT_HOLD_MS) {
    powered = undefined;
    powerCutAt = undefined;
  }
  if (voltage !== undefined && voltage < CUT_VOLTAGE && lastCharge !== true) {
    powered = false;
    powerCutAt = now;
  }
  return { powered, ignitionChangedAt, powerCutAt, powerPositionId: positionId ?? prev.powerPositionId, charge: lastCharge };
}

function applySticky(
  vehicleId: string,
  blocked: boolean | undefined,
  ignition: boolean | undefined,
  powerVoltage: number | undefined,
  powered: boolean | undefined,
  batteryLevel: number | undefined,
  charge?: boolean,
  positionId?: string,
): StickyState {
  const prev = stickyState.get(vehicleId) ?? {};
  const next: StickyState = {
    blocked: blocked !== undefined ? blocked : prev.blocked,
    ignition: ignition !== undefined ? ignition : prev.ignition,
    powerVoltage: powerVoltage !== undefined ? powerVoltage : prev.powerVoltage,
    batteryLevel: batteryLevel !== undefined ? batteryLevel : prev.batteryLevel,
    ...resolvePowered(prev, ignition, powered, charge, powerVoltage, positionId, Date.now()),
  };
  stickyState.set(vehicleId, next);
  return next;
}

// Ids para os quais já tentamos semear o cache pelo histórico nesta sessão
// do navegador — garante no máximo UMA chamada de histórico por veículo,
// nunca uma a cada poll (o poll de 15s / realtime só volta a chamar
// listFromTraccar, que não mexe nesse Set de novo).
const seededVehicleIds = new Set<string>();

// Chamado no logout — o cache é em memória do módulo (não expira, não é
// por-aba isolado de fato: sobrevive a troca de usuário na mesma aba, já
// que o logout não recarrega a página). Sem isso, um segundo usuário
// logando na mesma aba herdaria blocked/ignition vistos pela sessão
// anterior até o próximo pacote explícito chegar.
function clearStickyState(): void {
  stickyState.clear();
  seededVehicleIds.clear();
  lastKnownDevices.clear();
  lastKnownPositions.clear();
  tagDeviceIds = new Set();
  tagIdsFetchedAt = 0;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(TAG_IDS_CACHE_KEY);
      window.localStorage.removeItem(STICKY_SEED_CACHE_KEY);
      window.localStorage.removeItem(VEHICLE_LIST_CACHE_KEY);
    } catch {
      /* noop */
    }
  }
}

// --- Cache do seed sticky (blocked/ignition) entre F5s -----------------
//
// Achado 2026-09-04: `runStickySeedsInBackground` roda em TODO F5/login (o
// Set `seededVehicleIds` acima é só de memória do módulo, não sobrevive a
// reload) — medido ao vivo contra a frota real de 5000+ dispositivos, ~3000
// deles caem no "precisa semear" a cada carga, cada um disparando
// GET /api/positions?deviceId=X (histórico de 48h) contra Traccar/Postgres.
// Mesmo em lotes pequenos (ver STICKY_SEED_BATCH_SIZE), isso levou ~9
// minutos pra esvaziar em produção, competindo o tempo todo por
// capacidade do Traccar com qualquer outra tela (Usuários, Veículos, trocar
// de menu) — sintoma relatado: app "lento pra trocar de tela" e
// Usuários/Veículos "demorando pra carregar" logo após abrir o app.
// blocked/ignition raramente mudam de verdade e, quando mudam, um pacote ao
// vivo já sobrescreve o valor sticky na hora (ver applySticky) — então
// reaproveitar o que já foi resolvido em F5s anteriores é seguro e evita
// repetir a mesma busca de histórico pra quem já foi semeado. Guarda só
// blocked/ignition (nunca powerVoltage/batteryLevel/powered, que mudam com
// mais frequência e não são o gargalo aqui) — poucos KB pra 5000 devices,
// nada a ver com o payload de posição/frota inteira que fica de fora da
// persistência do React Query (ver query-persister.ts) por motivo de cota.
const STICKY_SEED_CACHE_KEY = "wp-sticky-seed-v1";
type StickySeedCache = Record<string, { blocked?: boolean; ignition?: boolean }>;

function loadStickySeedCache(): StickySeedCache {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STICKY_SEED_CACHE_KEY);
    return raw ? (JSON.parse(raw) as StickySeedCache) : {};
  } catch {
    return {};
  }
}

for (const [id, seed] of Object.entries(loadStickySeedCache())) {
  // Presença da chave = "já tentamos semear" (mesmo que blocked/ignition
  // continuem undefined, ex.: device sem nenhum histórico ainda) — só
  // registra em stickyState quando achou algo de fato, mas SEMPRE marca
  // como já semeado, senão um device sem histórico voltaria a ser tentado
  // em todo F5 pra sempre.
  if (seed.blocked !== undefined || seed.ignition !== undefined) stickyState.set(id, seed);
  seededVehicleIds.add(id);
}

// Escreve em lote (debounce), nunca a cada device individual — o próprio
// seed em segundo plano resolve às vezes centenas de devices em poucos
// segundos, um `setItem` síncrono por device recriaria no localStorage o
// mesmo tipo de rajada que estamos tentando evitar na rede.
let persistStickySeedTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePersistStickySeedCache(): void {
  if (typeof window === "undefined" || persistStickySeedTimer) return;
  persistStickySeedTimer = setTimeout(() => {
    persistStickySeedTimer = null;
    // Persiste TODO id já semeado nesta sessão, mesmo sem blocked/ignition
    // encontrado — a chave presente é o que evita repetir a busca de
    // histórico pra um device sem nada pra achar (ver comentário no load
    // acima). Um pacote ao vivo continua livre pra preencher o valor a
    // qualquer momento (applySticky), independente deste cache.
    const out: StickySeedCache = {};
    for (const id of seededVehicleIds) {
      const s = stickyState.get(id);
      out[id] = s ? { blocked: s.blocked, ignition: s.ignition } : {};
    }
    try {
      window.localStorage.setItem(STICKY_SEED_CACHE_KEY, JSON.stringify(out));
    } catch {
      /* noop */
    }
  }, 1000);
}

// Chamado (ver auth-context.tsx) quando o seed em segundo plano abaixo
// aprende um blocked/ignition que a lista de veículos ainda não tinha —
// pede um refetch pontual de ["vehicles"] pra UI pegar o valor sem esperar
// o próximo poll de 15s. Nunca chamado quando o histórico não traz nada de
// novo (ex: já tinha vindo ao vivo enquanto o histórico ainda carregava).
let stickyUpdatedHandler: (() => void) | null = null;
export function setStickyUpdatedHandler(handler: (() => void) | null): void {
  stickyUpdatedHandler = handler;
}

// Só dispara no cold start (primeira vez que o veículo aparece nesta sessão
// E a posição mais recente não trouxe o atributo E não há nada em cache
// ainda) — busca no histórico recente o último blocked/ignition explícito
// já reportado, pra não deixar a primeira renderização pós-F5 mostrar
// "desconhecido"/"desbloqueado" só porque o pacote mais novo não é do tipo
// que carrega o atributo.
function seedStickyIfNeeded(
  vehicleId: string,
  needsBlocked: boolean,
  needsIgnition: boolean,
): Promise<void> {
  if (!needsBlocked && !needsIgnition) return Promise.resolve();
  if (seededVehicleIds.has(vehicleId)) return Promise.resolve();
  seededVehicleIds.add(vehicleId);
  return positionsService
    .lastKnownAttributes(vehicleId)
    .then((found) => {
      const prev = stickyState.get(vehicleId) ?? {};
      const next: StickyState = {
        ...prev,
        blocked: prev.blocked !== undefined ? prev.blocked : found.blocked,
        ignition: prev.ignition !== undefined ? prev.ignition : found.ignition,
      };
      stickyState.set(vehicleId, next);
      schedulePersistStickySeedCache();
      // Só pede refetch se o histórico trouxe algo que o cache ainda não
      // tinha (prev undefined -> next definido) — se um poll ao vivo já
      // resolveu o atributo enquanto o histórico ainda carregava, `prev` já
      // vence acima e não há nada novo pra UI buscar.
      const learnedSomethingNew =
        (prev.blocked === undefined && next.blocked !== undefined) ||
        (prev.ignition === undefined && next.ignition !== undefined);
      if (learnedSomethingNew) stickyUpdatedHandler?.();
    })
    .catch(() => undefined);
}

// Dispara o seed de histórico de cada veículo que precisa — nunca aguardado
// por quem chama, roda inteiramente em segundo plano depois que
// listFromTraccar já retornou a lista pra UI. Sequencial puro (1 de cada
// vez) levava minutos pra esvaziar contra uma frota de milhares de
// veículos; um pool com concorrência fixa (versão anterior, 15 em voo o
// tempo todo) resolvia mais rápido mas virava uma rajada sustentada de
// requisições competindo com o próprio F5 (mount do mapa, primeira página
// da lista) — sticky é enriquecimento de segundo plano, não tem por que
// competir por rede/CPU logo nos primeiros segundos pós-F5. Lotes pequenos
// com pausa entre eles (ver runInBatches) espalham isso no tempo em vez de
// tudo de uma vez — "carrega aos poucos" também pro sticky seed, não só
// pro mapa (achado + correção no teste de carga de 5000 veículos, 2026-09-02).
const STICKY_SEED_BATCH_SIZE = 10;
const STICKY_SEED_BATCH_DELAY_MS = 300;

// Acima disto, nem tenta — pedido 2026-09-09 depois de um reinstall do app
// (que apaga o cache local, ver comentário acima) reabrir a mesma rajada
// documentada em 2026-09-04 contra a conta de teste (5006 devices, é
// Administrator do Traccar vendo a plataforma inteira, nunca uma frota real
// de cliente — ver [[gpsdrive_pwa_slowness_admin_fleet_2026-09-08]]):
// mesmo em lotes pequenos, ~9-25min de fundo competindo por rede com o
// resto do app (sessão, telas) a cada F5/reinstall/login novo. O cache
// localStorage (STICKY_SEED_CACHE_KEY) já torna isso um custo único — mas
// só depois da 1ª vez completar, e pra 5000+ devices essa 1ª vez sozinha já
// é grave demais. Nenhum cliente real deveria ter uma frota nessa escala;
// se um dia tiver, valeria a pena revisar este limite, não simplesmente
// aumentá-lo sem outra mudança (ex.: paginação real do seed).
const STICKY_SEED_MAX_FLEET_SIZE = 1000;

function runStickySeedsInBackground(
  devices: TraccarDevice[],
  positionByVehicleId: Map<string, Position>,
): void {
  if (devices.length > STICKY_SEED_MAX_FLEET_SIZE) return;
  const pending = devices
    .map((device) => {
      const id = String(device.id);
      const position = positionByVehicleId.get(id);
      const cached = stickyState.get(id);
      const needsBlocked = position?.blocked === undefined && cached?.blocked === undefined;
      const needsIgnition = position?.ignition === undefined && cached?.ignition === undefined;
      return { id, needsBlocked, needsIgnition };
    })
    .filter((d) => d.needsBlocked || d.needsIgnition);
  if (pending.length === 0) return;

  void runInBatches(pending, STICKY_SEED_BATCH_SIZE, STICKY_SEED_BATCH_DELAY_MS, ({ id, needsBlocked, needsIgnition }) =>
    seedStickyIfNeeded(id, needsBlocked, needsIgnition),
  );
}

// position ausente = dispositivo cadastrado no Traccar mas que ainda não
// reportou nenhuma posição GPS (rastreador não instalado/ligado ainda).
// Coordenada (0,0) = mesmo com um registro de posição, nunca houve um fix
// de GPS válido para esse dispositivo (positionsService já tenta cair no
// último fix válido do histórico antes de chegar aqui).
// Último bloqueio/desbloqueio mandado por SMS, gravado no device pelo
// backend (sms-send, recordBlockState) — só existe em equipamento que NÃO
// informa o estado do relé no protocolo (J4, pedido 2026-09-24: bloqueava
// de verdade mas a posição sempre dizia blocked:false). Quando presente,
// vence o atributo da posição.
function smsBlockedState(device: TraccarDevice): boolean | undefined {
  const v = device.attributes?.["gpsdrive.smsBlocked"];
  return typeof v === "boolean" ? v : undefined;
}

// --- Bloqueio confirmado pela resposta do comando ----------------------
//
// O GT06 responde o engineStop/engineResume em 2–4 s ("RELAY,1#=Success!",
// "Cut off the fuel supply: Success!"), mas só manda `blocked` no pacote de
// status, que vem a cada ~5 min (conferido no banco 2026-10-15). Sem isto a
// tela ficava "aguardando confirmação" até esse pacote e o operador
// reenviava o comando várias vezes. A resposta do rastreador passa a valer
// até chegar uma posição NOVA (id maior) com `blocked` explícito.
const COMMAND_BLOCK_OVERRIDE_MAX_MS = 15 * 60_000;
const commandBlockOverride = new Map<string, { blocked: boolean; at: number; positionId: number }>();

function positionBlocked(vehicleId: string, position?: Position): boolean | undefined {
  const override = commandBlockOverride.get(vehicleId);
  if (!override) return position?.blocked;
  const newerExplicit = position?.blocked !== undefined && Number(position.id) > override.positionId;
  if (newerExplicit || Date.now() - override.at > COMMAND_BLOCK_OVERRIDE_MAX_MS) {
    commandBlockOverride.delete(vehicleId);
    return position?.blocked;
  }
  return override.blocked;
}

const vehiclePatchListeners = new Set<(vehicles: Vehicle[]) => void>();

// Quem tem o queryClient (use-realtime-sync) aplica o patch no cache.
export function onVehiclePatch(listener: (vehicles: Vehicle[]) => void): () => void {
  vehiclePatchListeners.add(listener);
  return () => {
    vehiclePatchListeners.delete(listener);
  };
}

export function confirmBlockedFromCommand(vehicleId: string, blocked: boolean): void {
  const position = lastKnownPositions.get(vehicleId);
  commandBlockOverride.set(vehicleId, {
    blocked,
    at: Date.now(),
    positionId: position ? Number(position.id) || 0 : 0,
  });
  stickyState.set(vehicleId, { ...(stickyState.get(vehicleId) ?? {}), blocked });
  const device = lastKnownDevices.get(vehicleId);
  if (!device) return;
  const vehicle = toVehicle(device, position);
  vehiclePatchListeners.forEach((l) => l([vehicle]));
}

function toVehicle(device: TraccarDevice, position?: Position): Vehicle {
  const status = resolveStatus(device, position);
  const hasRealCoords = !!position && (position.lat !== 0 || position.lng !== 0);
  const icon = readIconAttributes(device.attributes);
  const sticky = applySticky(
    String(device.id),
    positionBlocked(String(device.id), position),
    position?.ignition,
    position?.power,
    powerStateFromAlarm(position?.alarm),
    position?.batteryLevel,
    position?.charge,
    position?.id,
  );
  const batteryLow =
    position?.alarm === "lowBattery" ||
    (sticky.batteryLevel != null && sticky.batteryLevel < LOW_BATTERY_THRESHOLD_PCT);
  return {
    id: String(device.id),
    name: device.name,
    plate: device.attributes?.plate ?? device.uniqueId,
    imei: device.uniqueId,
    phone: device.phone || undefined,
    isTag: tagDeviceIds.has(String(device.id)) || undefined,
    // attributes.driver é a fonte real (Traccar) — antes desta correção
    // ninguém lia essa chave de volta, então Vehicle.driver era sempre
    // undefined em produção mesmo quando gravado no cadastro.
    driver: typeof device.attributes?.driver === "string" ? device.attributes.driver : undefined,
    status,
    // wp.vehicleType (o iconKey real, ex.: "tractor"/"moto") é a fonte
    // oficial do tipo do veículo — attributes.type (legado) nunca é lido
    // aqui de propósito: era gravado no cadastro e podia divergir do ícone
    // de fato exibido (ver VEHICLE_FORM_ARCHITECTURE.md §3.1).
    type: resolveVehicleModel(icon.iconKey, undefined).category,
    iconKey: icon.iconKey,
    iconColorMode: icon.iconColorMode,
    iconColor: icon.iconColor,
    mapIconStyle: icon.mapIconStyle,
    speed: position?.speed ?? 0,
    lat: position?.lat ?? 0,
    lng: position?.lng ?? 0,
    lastConnection: device.lastUpdate ?? position?.timestamp ?? new Date(0).toISOString(),
    positionTimestamp: position?.timestamp,
    // blocked/ignition passam pelo cache sticky acima — nunca o atributo
    // cru da posição mais recente isolada (ver comentário do cache).
    ignition: sticky.ignition,
    moving: status === "online",
    bearing: position?.bearing,
    protocol: position?.protocol,
    hasPosition: hasRealCoords,
    blocked: smsBlockedState(device) ?? sticky.blocked,
    gpsSignal: position?.valid,
    hasAlerts: !!position?.alarm,
    batteryLow,
    // attributes.odometer é o valor real do rastreador (OBD) — preferido
    // sempre que o protocolo reporta. totalDistance é só a distância
    // acumulada que o próprio Traccar calcula a partir do GPS desde que o
    // dispositivo foi cadastrado (não é o odômetro real do veículo), usado
    // apenas como fallback quando o protocolo não manda attributes.odometer.
    odometer:
      position?.odometer != null
        ? Math.round(position.odometer / 1000)
        : position?.totalDistance != null
          ? Math.round(position.totalDistance / 1000)
          : undefined,
    // batteryLevel passa pelo cache sticky acima — nunca o atributo cru da
    // posição mais recente isolada (mesmo motivo do blocked/ignition/powerVoltage).
    batteryLevel: sticky.batteryLevel,
    // powerVoltage/powered passam pelo cache sticky acima — nunca o atributo
    // cru da posição mais recente isolada (mesmo motivo do blocked/ignition).
    powerVoltage: sticky.powerVoltage,
    batteryVoltage: position?.battery,
    hours: position?.hours,
    alarm: position?.alarm,
    powered: sticky.powered,
  };
}

// --- Snapshot da lista pra F5/login instantâneo --------------------------
//
// Pedido explícito do usuário 2026-09-04 (3ª vez pedindo isso): "toda vez
// que entro no sistema ou dou F5 fica carregando... quero que já apareça
// imediatamente". Guarda a ÚLTIMA resposta completa de listFromTraccar
// (devices + posições já mescladas em Vehicle[]) — usada como `initialData`
// pelas telas que consomem useVehicles()/["vehicles"] (ver hooks/queries e
// routes/index.tsx), pro F5 mostrar a frota conhecida na hora em vez de um
// "carregando"/"0 veículos" (ver gpsdrive_sticky_seed_flood_fix_2026-09-04
// na memória) enquanto o fetch real (sempre disparado por trás,
// initialDataUpdatedAt: 0 no consumidor) confirma o que mudou.
//
// Deliberadamente DIFERENTE da decisão em query-persister.ts de excluir
// ["vehicles"]/["positions"] da persistência automática do React Query: lá
// o risco era o PRÓPRIO React Query serializar o cache inteiro (todas as
// queries) a cada mudança de estado, inclusive a cada patch de WebSocket
// (várias vezes por segundo sob carga). Aqui é uma única chave, escrita só
// uma vez por poll completo (a cada 15s, nunca por mensagem de WebSocket
// isolada — ver useRealtimeSync, que só faz setQueryData local, não chama
// isto). Pra 5000+ devices um Vehicle[] serializado fica na casa de 1-2MB,
// confortável dentro da cota de localStorage.
const VEHICLE_LIST_CACHE_KEY = "gpsdrive.vehicleList.cache.v1";

export function readVehicleListCache(): Vehicle[] | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(VEHICLE_LIST_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Vehicle[]) : undefined;
  } catch {
    return undefined;
  }
}

function writeVehicleListCache(list: Vehicle[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VEHICLE_LIST_CACHE_KEY, JSON.stringify(list));
  } catch {
    // localStorage indisponível (aba anônima/quota) — cache é só otimização.
  }
}

async function listFromTraccar(): Promise<Vehicle[]> {
  void refreshTagDeviceIds();
  const [devices, positions] = await Promise.all([
    apiRequest<TraccarDevice[]>("/api/devices", { method: "GET", credentials: "include" }),
    positionsService.latest(),
  ]);
  const positionByVehicleId = new Map(positions.map((p) => [p.vehicleId, p]));

  // Espelha o poll completo no cache de patch incremental (ver
  // applyRealtimeUpdate) — toda mensagem de WebSocket a partir daqui
  // encontra o device/posição mais recentes já vistos, mesmo que a própria
  // mensagem só traga uma posição isolada.
  for (const device of devices) lastKnownDevices.set(String(device.id), device);
  for (const [id, position] of positionByVehicleId) lastKnownPositions.set(id, position);

  // Cold start (F5 / primeira carga desta sessão): para os devices cuja
  // posição mais recente não trouxe blocked/ignition E que ainda não têm
  // nada no cache sticky, busca o último valor confirmado no histórico —
  // mas em segundo plano, NUNCA aguardado aqui. A lista de veículos deve
  // renderizar imediatamente com o que já se sabe (posição ao vivo + cache
  // sticky existente); se e quando o histórico confirmar algo novo,
  // stickyUpdatedHandler (ver auth-context.tsx) pede um refetch pontual.
  runStickySeedsInBackground(devices, positionByVehicleId);

  // Todos os dispositivos cadastrados aparecem na lista, mesmo sem posição
  // ainda — só a exibição no mapa (marcadores) exige uma posição válida.
  const result = devices.map((device) => toVehicle(device, positionByVehicleId.get(String(device.id))));
  writeVehicleListCache(result);
  return result;
}

async function createInTraccar(input: {
  name: string;
  uniqueId: string;
  attributes?: Record<string, unknown>;
}): Promise<Vehicle> {
  const device = await apiRequest<TraccarDevice>("/api/devices", {
    method: "POST",
    credentials: "include",
    body: { name: input.name, uniqueId: input.uniqueId, attributes: input.attributes ?? {} },
  });
  // Vincula o dispositivo ao usuário logado — necessário para usuários não
  // administradores enxergarem o dispositivo em GET /api/devices depois.
  // Admins já veem tudo; se a chamada falhar por já existir/não ser
  // necessária, ignoramos (não deve bloquear o cadastro).
  const user = await authService.getSession().catch(() => null);
  if (user) {
    await apiRequest("/api/permissions", {
      method: "POST",
      credentials: "include",
      body: { userId: user.id, deviceId: device.id },
    }).catch(() => undefined);
  }
  // Multiempresa (2026-10-16): o veículo já nasce da empresa de quem
  // cadastrou (GET do metadado reivindica) — sem isso ficava "sem dono" até
  // alguém salvar foto/placa, e a equipe da empresa não o recebia.
  await gpsDriveGet(`/api/v1/vehicle-metadata/${device.id}`).catch(() => undefined);
  return toVehicle(device);
}

// Grava um patch de attributes direto no device do Traccar — para persistir
// de fato "vinculado ao dispositivo" (e não só no localStorage do navegador
// atual). PUT /api/devices/{id} do Traccar espera o objeto inteiro de volta,
// então buscamos o device atual primeiro e fazemos merge só das chaves que
// mudaram, preservando tudo o mais intacto. Base compartilhada por qualquer
// escrita de attribute fora do cadastro do GPS Drive Backend (ícone/cor do
// pino, motorista).
async function updateDeviceAttributesInTraccar(
  id: string,
  attributesPatch: Record<string, unknown>,
): Promise<Vehicle> {
  const device = await apiRequest<TraccarDevice>(`/api/devices/${id}`, {
    method: "GET",
    credentials: "include",
  });
  const updated = await apiRequest<TraccarDevice>(`/api/devices/${id}`, {
    method: "PUT",
    credentials: "include",
    body: { ...device, attributes: { ...(device.attributes ?? {}), ...attributesPatch } },
  });
  return toVehicle(updated);
}

// Renomeia o veículo de verdade no Traccar (device.name) — mesmo padrão de
// GET+PUT do helper acima. "Descrição / Nome" em VehicleFormPanel salvava só
// em vehicle_metadata.customName, que nada mais no app lê; o nome exibido em
// mapa/lista/app sempre vem daqui (v.name = device.name, ver toVehicle), por
// isso editar aquele campo parecia "não mudar nada" (bug relatado
// 2026-09-01: "quando eu mudo o nome do veículo ele não muda no sistema").
async function updateDeviceNameInTraccar(id: string, name: string): Promise<Vehicle> {
  const device = await apiRequest<TraccarDevice>(`/api/devices/${id}`, {
    method: "GET",
    credentials: "include",
  });
  const updated = await apiRequest<TraccarDevice>(`/api/devices/${id}`, {
    method: "PUT",
    credentials: "include",
    body: { ...device, name },
  });
  return toVehicle(updated);
}

// Corrige o IMEI (uniqueId) de verdade no Traccar — mesmo padrão GET+PUT dos
// helpers acima. Bug relatado 2026-09-15: tags cadastradas com o uniqueId sem
// os zeros à esquerda que o protocolo GT06 manda no pacote de login (ex:
// "3092526517" em vez de "000003092526517"), então o dispositivo nunca batia
// com nenhum device e as posições ficavam de fora ("Unknown device" no log do
// Traccar) — sem um jeito de corrigir o IMEI pela tela, só recriando o
// dispositivo do zero (perdendo histórico/config).
async function updateDeviceUniqueIdInTraccar(id: string, uniqueId: string): Promise<Vehicle> {
  const device = await apiRequest<TraccarDevice>(`/api/devices/${id}`, {
    method: "GET",
    credentials: "include",
  });
  const updated = await apiRequest<TraccarDevice>(`/api/devices/${id}`, {
    method: "PUT",
    credentials: "include",
    body: { ...device, uniqueId },
  });
  return toVehicle(updated);
}

// DELETE /api/devices/{id} do Traccar — remove o dispositivo de verdade
// (não é soft-delete): histórico de posições, comandos e permissões desse
// device somem junto. Não mexe em vehicle_metadata do GPS Drive Backend
// (fica órfão lá, sem endpoint de remoção hoje — fora do escopo pedido).
async function deleteFromTraccar(id: string): Promise<void> {
  await apiRequest<void>(`/api/devices/${id}`, { method: "DELETE", credentials: "include" });
}

// Aplica um delta de WebSocket (devices/positions no formato bruto do
// Traccar, ver use-realtime-sync.ts) direto sobre o cache local, SEM
// refetch — usado pra atualizar só os veículos que de fato mudaram numa
// frota grande, em vez de invalidar ["vehicles"] inteiro a cada mensagem
// (isso forçava GET /api/devices + /api/positions da frota inteira e
// reconstruía todo mundo pra 1 posição mudando — gargalo real medido no
// teste de carga de 5000 veículos, 2026-09-02: ~1.7-2s de bloqueio da main
// thread por mensagem). Reconstrói o Vehicle com o MESMO toVehicle usado
// pelo poll completo (nunca uma derivação paralela/divergente), preenchendo
// o lado que a mensagem não trouxe (device ou posição) com o último
// conhecido em lastKnownDevices/lastKnownPositions. Devolve só os veículos
// afetados; ids nunca vistos antes (nem por um poll, nem por uma mensagem
// de devices anterior) são ignorados aqui — o poll de 15s pega esses.
export function applyRealtimeUpdate(payload: {
  devices?: TraccarDevice[];
  positions?: TraccarPosition[];
}): Vehicle[] {
  const touchedIds = new Set<string>();

  for (const device of payload.devices ?? []) {
    lastKnownDevices.set(String(device.id), device);
    touchedIds.add(String(device.id));
  }

  for (const raw of payload.positions ?? []) {
    const position = positionsService.toPosition(raw);
    // Mesma degradação graciosa de positionsService.latest() pra um fix sem
    // GPS válido (comum em keep-alives), só que sem a chamada de histórico
    // extra: reaproveita a última coordenada válida já em cache em vez de
    // buscar no servidor — uma mensagem de tempo real não pode disparar
    // requisição de rede extra por si só.
    const prev = lastKnownPositions.get(position.vehicleId);
    const resolved = position.valid === false && prev ? { ...position, lat: prev.lat, lng: prev.lng, bearing: prev.bearing } : position;
    lastKnownPositions.set(resolved.vehicleId, resolved);
    touchedIds.add(resolved.vehicleId);
  }

  const updated: Vehicle[] = [];
  for (const id of touchedIds) {
    const device = lastKnownDevices.get(id);
    if (!device) continue;
    updated.push(toVehicle(device, lastKnownPositions.get(id)));
  }
  return updated;
}

// O status (online/parado/offline) só era calculado quando chegava dado
// novo (poll ou WebSocket). Rastreador que simplesmente PARA de falar não
// manda nada — então ele ficava verde na tela até o próximo poll completo,
// que em aba em segundo plano/PC voltando de suspensão pode demorar bem
// mais que 15s (pedido 2026-10-19: "mostra online mas já estava offline,
// tenho que dar F5"). Reavalia localmente, sem rede, só quem mudou de
// status com o passar do tempo (ex.: passou dos 5 min sem reportar).
export function refreshAgedStatuses(current: Vehicle[]): Vehicle[] {
  const updated: Vehicle[] = [];
  for (const v of current) {
    const device = lastKnownDevices.get(v.id);
    if (!device) continue;
    const position = lastKnownPositions.get(v.id);
    if (resolveStatus(device, position) !== v.status) updated.push(toVehicle(device, position));
  }
  return updated;
}

export const vehiclesService = {
  list: () => (API_URL ? listFromTraccar() : Promise.resolve(mockVehicles)),
  applyRealtimeUpdate,
  refreshAgedStatuses,
  get: (id: string) =>
    apiGet<Vehicle>(`/vehicles/${id}`, mockVehicles.find((v) => v.id === id) as Vehicle),
  update: (id: string, data: Partial<Vehicle>) => apiPut<Vehicle>(`/vehicles/${id}`, data),
  create: (input: { name: string; uniqueId: string; attributes?: Record<string, unknown> }) =>
    createInTraccar(input),
  updateIconConfig: (
    id: string,
    patch: Partial<Pick<Vehicle, "iconKey" | "iconColorMode" | "iconColor" | "mapIconStyle">>,
  ) =>
    API_URL
      ? updateDeviceAttributesInTraccar(id, mergeIconAttributes(undefined, patch))
      : Promise.resolve(undefined),
  // Escrita genérica de attributes do device — hoje usada pelo campo
  // Motorista (wp.vehicleType/plate seguem seus próprios fluxos dedicados).
  updateAttributes: (id: string, patch: Record<string, unknown>) =>
    API_URL ? updateDeviceAttributesInTraccar(id, patch) : Promise.resolve(undefined),
  updateName: (id: string, name: string) =>
    API_URL ? updateDeviceNameInTraccar(id, name) : Promise.resolve(undefined),
  updateUniqueId: (id: string, uniqueId: string) =>
    API_URL ? updateDeviceUniqueIdInTraccar(id, uniqueId) : Promise.resolve(undefined),
  remove: (id: string) => deleteFromTraccar(id),
  clearStickyState,
  // Chamado ao salvar o cadastro do veículo (Tipo de rastreador pode ter
  // virado/deixado de ser "tag") — aplica na hora, sem esperar 2min.
  refreshTagDeviceIds: () => refreshTagDeviceIds(true),
};
